import CloseIcon from '@mui/icons-material/Close';
import BackIcon from '@mui/icons-material/ArrowBack';
import {
  CircularProgress,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { FC, ReactNode, useCallback } from 'react';
import Button from 'components/Button';
import { useLanguage } from 'context/AppContext';
import useKeypressListener from 'hooks/useKeypressListener';

import type { DialogProps as MuiDialogProps, Theme as MUITheme } from '@mui/material';
import type { SxProps } from '@mui/system';
import theme from 'App.theme';

export type DialogProps = Partial<MuiDialogProps> & {
  open: boolean;
  title?: string;
  heading?: ReactNode;
  body?: ReactNode; // TODO: SS when we update this component, just use children instead of bodys
  cancelLabel?: string;
  cancelDisabled?: boolean;
  cancelIsDestructive?: boolean;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  allowClose?: boolean;
  onEnterKey?: 'cancel' | 'confirm'; // TODO: SS onEnterKey should be confirm by default as it's a confirm dialog and that is the expected behavior
  onClose?: VoidFunction; // trigger a close action without triggering the cancel action
  onCancel?: VoidFunction;
  onConfirm?: VoidFunction;
  onBack?: VoidFunction;
  contentSx?: SxProps<MUITheme>; // TODO: SS export each component, ie. DialogTitle, DialogContent, DialogActions, separately then we can compose our UI and not worry about passing styles to each
  actionsSx?: SxProps<MUITheme>;
};

// generic info or confirm dialog
const Dialog: FC<DialogProps> = ({
  title = '',
  heading,
  children,
  body,
  cancelLabel,
  cancelDisabled,
  cancelIsDestructive = true,
  confirmLabel,
  confirmDisabled,
  confirmLoading,
  allowClose = true,
  onEnterKey,
  onCancel,
  onClose,
  onConfirm,
  onBack,
  contentSx,
  actionsSx,
  ...dialogProps
}) => {
  const [lang] = useLanguage();

  const confirmLabelToUse = confirmLabel || lang.Dialog.Confirm;
  const cancelLabelToUse = cancelLabel || lang.Dialog.Cancel;

  const handleEnterKeypress = useCallback(() => {
    if (onEnterKey == 'confirm') onConfirm?.();
    else if (onCancel) onCancel();
  }, [onEnterKey, onCancel, onConfirm]);

  useKeypressListener({
    keyOrKeys: 'Enter',
    callback: handleEnterKeypress,
    condition: !!(dialogProps.open && onEnterKey && (onEnterKey != 'confirm' || (onConfirm && !confirmDisabled))),
  });

  return (
    <MuiDialog fullWidth maxWidth="xs" onClose={allowClose ? onClose || onCancel : undefined} {...dialogProps}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {onBack && (
          <IconButton
            role="button"
            aria-label="back"
            onClick={onBack}
            sx={{
              margin: '-4px 8px 0 -8px',
              color: theme.greys.GREY4,
              '&:hover': {
                color: theme.colors.active,
              },
            }}
          >
            <BackIcon />
          </IconButton>
        )}

        {title}

        {allowClose && (onClose || onCancel) && (
          <IconButton
            role="button"
            aria-label="close"
            onClick={onClose || onCancel}
            sx={{
              alignSelf: 'flex-start',
              marginLeft: 'auto', // makes the above align-self effective
              marginTop: '-4px', // fix alignment but keep button padding
              marginRight: '-8px', // fix alignment but keep button padding
              color: theme.greys.GREY1,
              '&:hover': {
                backgroundColor: 'initial',
                color: theme.colors.black,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={contentSx}>
        {heading}

        {body || children}
      </DialogContent>

      {(onCancel || onConfirm) && (
        <DialogActions
          sx={{
            padding: `${theme.layout.spacing.medium} 24px`,
            ...actionsSx,
          }}
        >
          {onCancel && (
            <Button
              name={cancelLabelToUse}
              colors={cancelIsDestructive ? 'white on red' : 'active on white'}
              disabled={cancelDisabled}
              onClick={onCancel}
            >
              {cancelLabelToUse}
            </Button>
          )}

          {!!onConfirm && (
            <Button name={confirmLabelToUse} onClick={onConfirm} disabled={confirmDisabled} colors="white on active">
              {confirmLoading ? <CircularProgress color="inherit" size={16} /> : confirmLabelToUse}
            </Button>
          )}
        </DialogActions>
      )}
    </MuiDialog>
  );
};

export default Dialog;
